package com.neurobs.ccabserver.administrator.components

import com.neurobs.ccab.{StudySessionManager, TestSessionProperties, db}
import com.neurobs.errors.toOption
import com.neurobs.js.util.StringUtilities.{dateStringWithToday, timeString}
import com.neurobs.react.{Link, Table, TableCell, TableHeader, TableRow}
import com.neurobs.util.{Guid, SecondsTimestamp}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import io.circe.generic.auto._
import slinky.web.html.span

/**
 * Displays a table containing the given sessions, with a resume link for
 * those that are resumable.
 */
@react object SessionsHistoryForm {
	/**
	 * @param history Sessions to display
	 * @param resumableSubjectGuids The subject GUIDs that are resumable
	 * @param onResumeClick Called when a resume link is clicked
	 */
	case class Props
	(
		history: StudySessionManager.SubjectSessionHistory,
		resumableSubjectGuids: Seq[Guid],
		onResumeClick: db.SessionData => Unit
	)

	val component = FunctionalComponent[Props] { props =>
		val resumable = props.history.logicalSessions.exists(_.combinedSession.exists(_.tsSessions.exists(s => props.resumableSubjectGuids.contains(s.data.subjectGuid))))
		if (props.history.logicalSessions.isEmpty)
			"None"
		else
			Table(
				TableHeader(
					TableCell("Start Time").withKey(key = "st"),
					TableCell("Session").withKey(key = "s"),
					toOption(resumable)(TableCell("").withKey(key = ""))
				).withKey(key = "h"),
				props.history.logicalSessions.map { s =>
					TableRow(
						TableCell(startTimeString(s.startTime)).withKey(key = "st"),
						TableCell(s.shortDescription).withKey(key = "s"),
						toOption(resumable) {
							val tsSessions = s.resumableTSSessions(props.resumableSubjectGuids)
							if (tsSessions.isEmpty)
								TableCell().withKey(key = "l")
							else if (tsSessions.size == 1)
								TableCell(Link(caption = "Resume", onClick = () => props.onResumeClick(tsSessions.head))).withKey(key = "l")
							else {
								TableCell(
									tsSessions map { s =>
										span(
											Link(caption = "Resume", onClick = () => props.onResumeClick(s)),
											s" (${startTimeString(s.startTime)}) "
										)
									}
								).withKey(key = "l")
							}
						}
					).withKey(key = s.key)
				}
			)
	}

	private def startTimeString(t: SecondsTimestamp): String = s"${dateStringWithToday(t)} ${timeString(t)}"
}


/**
 * Displays a table containing the given sessions, with a resume link for
 * those that are resumable.
 */
@react object PreviousSessionsForm {
	/**
	 * @param sessions Sessions to display
	 * @param resumableSubjectGuids The subject GUIDs that are resumable
	 * @param onResumeClick Called when a resume link is clicked
	 */
	case class Props
	(
		sessions: Seq[db.SessionData],
		resumableSubjectGuids: Seq[Guid],
		onResumeClick: db.SessionData => Unit
	)

	val component = FunctionalComponent[Props] { props =>
		val resumable = props.sessions.exists(s => props.resumableSubjectGuids.contains(s.subjectGuid))
		if (props.sessions.isEmpty)
			"None"
		else
			Table(
				TableHeader(
					TableCell("Start Time").withKey(key = "st"),
					TableCell("Sequence").withKey(key = "s"),
					toOption(resumable)(TableCell("").withKey(key = ""))
				).withKey(key = "h"),
				props.sessions.map { s =>
					TableRow(
						TableCell(startTimeString(s)).withKey(key = "st"),
						TableCell(sequenceString(s)).withKey(key = "s"),
						toOption(resumable)(
							if (props.resumableSubjectGuids.contains(s.subjectGuid))
								TableCell(Link(caption = "Resume", onClick = () => props.onResumeClick(s))).withKey(key = "l")
							else
								TableCell().withKey(key = "l")
						)
					).withKey(key = s.id.toString)
				}
			)
	}

	private def startTimeString(sess: db.SessionData): String = s"${dateStringWithToday(sess.startTime)} ${timeString(sess.startTime)}"
	private def sequenceString(sess: db.SessionData): String = {
		val props = sess.properties.map { json =>
			io.circe.parser.decode[TestSessionProperties](json) match {
				case Right(p) => p.group + " - " + p.sequence + p.config.map("(" + _ + ")").getOrElse("")
				case _ => "Unknown"
			}
		}
		props.getOrElse("Unknown")
	}
}
